import type { ComputedCommandsSection, PaletteDef } from '../types';
import { type MatchCommandsOptions, matchCommands, sortAndFilterSections } from './util';

/**
 * Compute sections for a given palette and query.
 */
function computeSections(
    palette: PaletteDef,
    query: string,
    options: MatchCommandsOptions
): ComputedCommandsSection[] {
    return palette.groups.map((group) => {
        const commands =
            typeof group.commands === 'function'
                ? group.commands(palette.query.text)
                : group.commands;
        const matchingCommands = matchCommands(query, commands, options);
        // the priority is the highest matching score of the commands in the group
        const priority = matchingCommands.reduce(
            (prev, x) =>
                typeof x.score !== 'undefined' ? Math.max((1 - x.score) * 10, prev) : prev,
            group.priority || 5
        );

        return {
            group,
            priority,
            data: matchingCommands.map((r) => r.item),
        };
    });
}

/**
 * Create a palette with filtered commands based on the query.
 * It is useful if you need to create a full controlled palette.
 */
export function createFilteredPalette(
    palette: Omit<PaletteDef, 'query'>,
    query: string,
    options: MatchCommandsOptions = {}
): PaletteDef {
    const paletteWithQuery = {
        ...palette,
        // This text is used if the palette filters itself
        // in this case we don't want to use it.
        query: { text: '' },
    };
    if (query === '') {
        return paletteWithQuery;
    }

    return {
        ...paletteWithQuery,
        groups: sortAndFilterSections(computeSections(paletteWithQuery, query, options)).map(
            (match) => match.group
        ),
    };
}
